import Papa from 'papaparse';
import * as pdfjsLib from 'pdfjs-dist';
import { Mission, MissionStop } from '../data/missions';
import { STATIONS } from '../data/stations';
import { localApi } from './localApi';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.4.624/build/pdf.worker.min.mjs';

// Helper pour normaliser le texte (supprime accents, miniscule, et SURTOUT la ponctuation/espaces)
// Cela résout les problèmes d'encodage Excel (espaces insécables, apostrophes remplacées par des symboles \uFFFD)
function normalizeText(str: string): string {
    return str.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "") // Supprime TOUT ce qui n'est pas alphanumérique
        .trim();
}

// Dictionnaire d'alias pour faire correspondre les noms probables présents dans le CSV (souvent simplifiés)
// aux vrais noms complexes de STATIONS.
const STATION_ALIASES: Record<string, string> = {
    "ile rousse": "LISULA ROSSA",
    "l'ile rousse": "LISULA ROSSA",
    "ile-rousse": "LISULA ROSSA",
    "davia": "A Marina di Davia",
    "marina di davia": "A Marina di Davia",
    "aregno": "Aregnu",
    "algajola": "L’Algaghjola",
    "algaghjola": "L’Algaghjola",
    "sant ambrogio": "Sant’Ambrosgiu",
    "ambrosgiu": "Sant’Ambrosgiu",
    "marina di sant ambrogio": "A Marina di Sant’Ambrosgiu",
    "giorgio": "Ghjorghju",
    "lumio": "Lumiu l’Arinella",
    "lumiu": "Lumiu l’Arinella",
    "arinella": "Lumiu l’Arinella",
    "santa-restituta": "Santa Restituta",
    "fiumeseccu": "U Fiumeseccu Alzeta (GR 20)",
    "gr 20": "U Fiumeseccu Alzeta (GR 20)",
    "camp rafali": "U Fiumeseccu Alzeta (GR 20)",
    "alzeta suprana": "Calvi L’Alzeta Suprana",
    "calvi u pinetu": "Calvi U Pinetu",
    "pinetu": "Calvi U Pinetu",
    "calvi e padule": "Calvi E Padule",
    "padule": "Calvi E Padule",
    "calvi talle": "Calvi Talle",
    "talle": "Calvi Talle",
    "ponte leccia": "Ponte-Leccia",
    "ponteleccia": "Ponte-Leccia",
    // Corsican/French/Alternative name matches for incomplete stop lists
    "lupinu": "Lupino",
    "montesoru": "Montesoro",
    "erbaghjolu": "Erbajolo",
    "sansonetti - puliclinica": "Sansonetti- Polyclinique",
    "sansonetti puliclinica": "Sansonetti- Polyclinique",
    "u saltatoghju": "Saltatojo",
    "saltatoghju": "Saltatojo",
    "prigione di u borgu-r": "Maison d'arrêt Borgo",
    "prigione di u borgu-rasignani": "Maison d'arrêt Borgo",
    "u borgu": "BORGO",
    "lucciana l'alivella": "Lucciana Olivella",
    "u precoghju-spaziu": "Complexe sportif de Lucciana",
    "ponte novu": "PONTE-NOVO",
    "u ponte a a leccia": "Ponte-Leccia",
    "u ponte à a leccia": "Ponte-Leccia",
    "francardu": "FRANCARDO",
    "corti": "CORTE",
    "u poghju a riventosa": "Poggio-Riventosa",
    "poghju a riventosa": "Poggio-Riventosa",
    "venacu": "VENACO",
    "vivariu": "VIVARIO",
    "savaghju": "Savaggio",
    "bucugna": "BOCOGNANO",
    "bucugnà": "BOCOGNANO",
    "aucciani": "Ucciani",
    "mezana": "Mezzana",
    "u ricantu": "Ricanto",
    "i salini": "Les Salines",
    "aiacciu": "Ajaccio",
    "nuvella": "Novella",
    "belgude": "Belgodère",
    "belgude u pozzatellu": "Pozzatellu",
    "u reginu": "Reginu",
    "suveria": "Soveria",
    "munticellu": "Monticellu",
    "monticello": "Monticellu",
    "monticellu": "Monticellu",
    "u precoghju s.spurtivu": "Complexe sportif de Lucciana",
    "u precoghju s. spurtivu": "Complexe sportif de Lucciana",
    "u precoghju s spurtivu": "Complexe sportif de Lucciana",
    "u precoghju-s.spurtivu": "Complexe sportif de Lucciana",
    "sansonetti-puliclinica": "Sansonetti- Polyclinique"
};

// On génère un dictionnaire dont les clés sont ultra-nettoyées (sans espaces, sans ponctuation)
const CLEAN_ALIASES: Record<string, string> = {};
for (const [key, val] of Object.entries(STATION_ALIASES)) {
    CLEAN_ALIASES[key.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()] = val;
}

/**
 * Analyse un fichier CSV pour extraire des missions.
 * Format attendu du CSV :
 * 
 * Ligne 1 : En-têtes (ignorée pour les croisements si pas un train) -> Les colonnes avec des numéros de trains (ex: "801", "803") sont détectées.
 * Colonne 1 : Noms des Gares (doivent correspondre approx. à STATIONS)
 * Cellules (X,Y) : Heure d'arrivée au format HH:MM
 */
export function parseMissionsFromGrid(data: string[][]): Mission[] {
    if (data.length < 2) {
        return [];
    }

    const headers = data[0]; // Ligne 1 = Numéros de trains attendus
    const missions: Mission[] = [];

    // On cherche quelles colonnes correspondent à des trains (ex: en-tête contient des chiffres)
    const trainColumns: { colIndex: number, trainNumber: string }[] = [];
    headers.forEach((header, index) => {
        // Si l'en-tête est vide ou ressemble à "Gare", on ignore.
        const match = header.match(/\d{2,}[a-zA-Z]*/);
        if (match) {
            trainColumns.push({ colIndex: index, trainNumber: match[0] });
        }
    });

    if (trainColumns.length === 0) {
        console.warn("Aucun numéro de train détecté dans les en-têtes (Ligne 1). Assurez-vous d'avoir des colonnes comme '801'.", headers);
        return []; // Pas de missions trouvées
    }

    // Prépare un tableau d'arrêts pour chaque train détecté
    const stopsPerTrain = new Map<number, MissionStop[]>();
    trainColumns.forEach(tc => stopsPerTrain.set(tc.colIndex, []));

    // Parcourir les lignes suivantes pour trouver les gares et croiser avec les horaires
    for (let r = 1; r < data.length; r++) {
        const row = data[r];
        if (row.length === 0) continue;

        const stationNameRaw = row[0]; // Colonne 1 = Gare supposée
        if (!stationNameRaw) continue;
        const stationNameNorm = normalizeText(stationNameRaw);

        // 1. Chercher d'abord une correspondance EXACTE avec le nom original normalisé !
        let matchedStation = STATIONS.find(s => normalizeText(s.name) === stationNameNorm);

        if (!matchedStation) {
            // Sinon, chercher via les alias et l'inclusion
            let targetStationName = stationNameNorm;

            // Si la chaine super propre correspond à un alias
            if (CLEAN_ALIASES[stationNameNorm]) {
                targetStationName = normalizeText(CLEAN_ALIASES[stationNameNorm]);
            } else {
                // Chercher un alias par inclusion (si le csv dit "arret davia", ça inclut "davia")
                for (const [alias, realName] of Object.entries(CLEAN_ALIASES)) {
                    if (stationNameNorm.includes(alias)) {
                        targetStationName = normalizeText(realName);
                        break;
                    }
                }
            }

            // 2. Chercher une correspondance exacte avec le nom de l'alias ciblé
            matchedStation = STATIONS.find(s => normalizeText(s.name) === targetStationName);

            // 3. Si non trouvé de façon exacte, filtrer par inclusion (fallback)
            if (!matchedStation) {
                matchedStation = STATIONS.find(s => {
                    const sNameNorm = normalizeText(s.name);
                    return sNameNorm.includes(targetStationName) || targetStationName.includes(sNameNorm);
                });
            }
        }

        if (matchedStation) {
            // C'est bien une ligne de gare. On vérifie les horaires pour chaque colonne de train.
            trainColumns.forEach(tc => {
                const timeRaw = row[tc.colIndex];
                if (timeRaw) {
                    // Chercher un motif horaire HH:MM ou HHhMM
                    const timeMatch = timeRaw.match(/(\d{1,2})[:hH\s](\d{2})/);
                    if (timeMatch) {
                        const hours = timeMatch[1].padStart(2, '0');
                        const minutes = timeMatch[2];
                        const stops = stopsPerTrain.get(tc.colIndex);
                        stops?.push({
                            stationName: matchedStation.name,
                            arrivalTime: `${hours}:${minutes}`,
                            pk: matchedStation.pk
                        });
                    }
                }
            });
        }
    }

    // Construire les missions finales
    trainColumns.forEach((tc, idx) => {
        const stops = stopsPerTrain.get(tc.colIndex);
        if (stops && stops.length >= 2) {
            // Trier les arrêts par heure de passage pour recréer l'ordre
            stops.sort((a, b) => a.arrivalTime.localeCompare(b.arrivalTime));

            const first = stops[0];
            const last = stops[stops.length - 1];
            const lineName = `${first.stationName} - ${last.stationName}`;

            missions.push({
                id: Date.now().toString() + '_' + idx + '_' + Math.random(),
                trainNumber: tc.trainNumber,
                lineName: lineName,
                stops: stops
            });
        }
    });

    return missions;
}

/**
 * Analyse un fichier CSV pour extraire des missions.
 */
export async function parseMissionsFromCSV(file: File): Promise<Mission[]> {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            skipEmptyLines: true,
            complete: (results) => {
                try {
                    const data = results.data as string[][];
                    const missions = parseMissionsFromGrid(data);
                    resolve(missions);
                } catch (err) {
                    console.error("Erreur de parsing CSV:", err);
                    reject(err);
                }
            },
            error: (err) => {
                console.error("PapaParse error:", err);
                reject(err);
            }
        });
    });
}

/**
 * Analyse un fichier PDF pour extraire des missions (via PDF.js local).
 */
export async function parseMissionsFromPDF(file: File): Promise<Mission[]> {
    try {
        const grid = await localApi.convertMissionsPdf(file);
        return parseMissionsFromGrid(grid);
    } catch (err) {
        console.error("Erreur de conversion PDF côté serveur:", err);
        throw err;
    }
}
